import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Box,
  Snackbar,
  Alert,
  ThemeProvider,
  Grid,
  Card,
  CardHeader,
  CardContent,
  FormHelperText,
} from "@mui/material";
import { createTheme } from "@mui/material/styles";
import AssignmentIndIcon from "@mui/icons-material/AssignmentInd";

const SalesRepForm = ({
  initialValues,
  setFormData,
  onSubmit,
  isLoading,
  errors,
  successMessage,
  errorMessage,
  isCreate,
  isUpdate,
  isView,
  isDelete,
  onCancel,
}) => {
  const [formData, setLocalFormData] = useState(initialValues);

  const navigate = useNavigate();

  useEffect(() => {
    setLocalFormData(initialValues);
  }, [initialValues]);

  const theme = createTheme({
    palette: {
      primary: {
        main: "#1e5d45", // Custom primary color
      },
    },
  });

  const capitalizeWords = (str) => {
    return str
      .split(" ")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;

    setLocalFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));

    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleInputChangeWithCapitalization = (e) => {
    const { name, value } = e.target;
    const capitalizedValue = capitalizeWords(value);
    handleInputChange({ target: { name, value: capitalizedValue } });
  };

  return (
    <ThemeProvider theme={theme}>
      {/* Success Snackbar */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="success"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      {/* Loading Snackbar */}
      <Snackbar
        open={isLoading}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="info"
          icon={<CircularProgress size={18} />}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          Loading, please wait...
        </Alert>
      </Snackbar>

      {/* Error Snackbar */}
      <Snackbar
        open={!!errorMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity="error"
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "auto",
            maxWidth: "100%",
            mx: "auto",
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit(e);
        }}
        noValidate
        autoComplete="off"
      >
        <Card
          variant="outlined"
        >
          <CardHeader
            title={
              <Typography
                variant="h6"
              >
                Basic Information
              </Typography>
            }
            sx={{ padding: "16px" }}
          />
          <CardContent sx={{ padding: "20px" }}>
            <Grid container spacing={2}>
              {/* First Name */}
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <TextField
                    label="First Name"
                    name="firstname"
                    value={formData.firstname}
                    onChange={handleInputChangeWithCapitalization}
                    error={!!errors.firstname}
                    helperText={errors.firstname}
                    disabled={isView || isDelete || isLoading}
                    required
                    size="small"
                  />
                </FormControl>
              </Grid>
              {/* Last Name */}
              <Grid item xs={4}>
                <FormControl fullWidth>
                  <TextField
                    label="Last Name"
                    name="lastname"
                    value={formData.lastname}
                    onChange={handleInputChangeWithCapitalization}
                    error={!!errors.lastname}
                    helperText={errors.lastname}
                    disabled={isView || isDelete || isLoading}
                    required
                    size="small"
                  />
                </FormControl>
              </Grid>
              {/* Branch */}
              <Grid item xs={2}>
                <FormControl fullWidth size="small">
                  <InputLabel id="branch-label" shrink>
                    Branch
                  </InputLabel>
                  <Select
                    labelId="branch-label"
                    label="Branch"
                    name="branch"
                    value={formData.branch}
                    onChange={handleInputChange}
                    error={!!errors.branch}
                    disabled={isView || isDelete || isLoading}
                    required
                  >
                    <MenuItem value="QC">QC</MenuItem>
                    <MenuItem value="Main">Main</MenuItem>
                  </Select>
                  {errors.branch && (
                    <FormHelperText error>{errors.branch}</FormHelperText>
                  )}
                </FormControl>
              </Grid>
              {/* Form buttons */}
              <Grid
                item
                xs={12}
                display="flex"
                justifyContent="flex-end"
                alignItems="center"
                mt={4}
                mb={2}
                mr={2}
              >
                {isView && (
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onCancel}
                    disabled={isLoading}
                    sx={{
                      marginLeft: "10px",
                      fontSize: "0.875rem",
                      padding: "8px 16px",
                    }}
                  >
                    Back
                  </Button>
                )}
                {!isView && (
                  <Button
                    variant="outlined"
                    onClick={onCancel} // Use the callback passed as a prop
                    disabled={isLoading}
                    sx={{
                      fontSize: "0.875rem",
                      padding: "8px 16px",
                      color: "#FF4C4C",
                      borderColor: "#FF4C4C",
                    }}
                  >
                    Cancel
                  </Button>
                )}
                {!isView && (
                  <Button
                    variant="contained"
                    color="primary"
                    type="submit"
                    disabled={isLoading}
                    sx={{
                      marginLeft: "10px",
                      fontSize: "0.875rem",
                      padding: "8px 16px",
                    }}
                  >
                    {isUpdate
                      ? "Update Sales Representative"
                      : isCreate
                      ? "Create Sales Representative"
                      : isDelete
                      ? "Delete Sales Representative"
                      : "Submit"}
                  </Button>
                )}
              </Grid>
            </Grid>
          </CardContent>
        </Card>          
      </form>
    </ThemeProvider>
  );
};

export default SalesRepForm;
